import React, { useEffect, useState } from 'react';
import { TextField, Button, SelectChangeEvent, Paper, Typography, IconButton, Box, Grid2, Link } from '@mui/material';
import { Add, AddCircleOutline, Cancel, Edit, MeetingRoom, Save, Send } from '@mui/icons-material';
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useLocation, useNavigate } from 'react-router-dom';
import { APICallStatus, Violation, ViolationStage, ViolationStatus, ViolationType } from '../../Types/UnitFeatureTypes';
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import DropdownSelection from '../CommonComponents/DropdownSelection';
import { useDispatch, useSelector } from 'react-redux';
import { createActionViolationAPIStatus, fetchUnit, statusUpdateViolationAPIStatus } from '../../Redux/selectors';
import { Actions } from '../../Redux/Reducer/rootReducer';
import Editor from '../TextEditor/Editor';
import { useApp } from '../../context/appContext';

const CreateViolation: React.FC = () => {
  const [formData, setFormData] = useState<Violation>({
    id: 0,
    title: '',
    description: '',
    possible_penalties: '',
    created_on: '',
    updated_on: '',
    violation_date: new Date(),
    due_date: undefined,
    unit: {
      id: 0,
      unit_number: '',
    },
    type: ViolationType.GENERAL,
    other_type: '',
    stage: ViolationStage.WRITTEN_WARNING,
    resolution_date: undefined,
    status: ViolationStatus.OPEN,
  })

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const unitData = useSelector(fetchUnit);
  const [showLastActionDatePicker, setShowLastActionDatePicker] = useState(false);
  const violationSavedAPI = useSelector(createActionViolationAPIStatus);
  const { setSnackbar } = useApp();


  useEffect(() => {
    if (violationSavedAPI === APICallStatus.SUCCESS) {
      navigate(-1);
      setSnackbar(true, 'Violation created successfully', 'success');
      dispatch(Actions.violation.resetStatusRequestAction());
    } else if (violationSavedAPI === APICallStatus.FAILURE) {
      setSnackbar(true, 'Failed to create violation', 'error');
      dispatch(Actions.violation.resetStatusRequestAction());
    }
  }, [violationSavedAPI]);

  useEffect(() => {
    if (location.state && location.state.edit) {
      const violation = location.state.violation as Violation;
      setFormData({
        id: violation.id,
        title: violation.title,
        description: violation.description,
        possible_penalties: violation.possible_penalties,
        created_on: violation.created_on,
        updated_on: violation.updated_on,
        violation_date: violation.violation_date,
        due_date: violation.due_date,
        unit: {
          id: violation.unit.id,
          unit_number: violation.unit.unit_number,
        },
        type: violation.type,
        other_type: violation.other_type,
        stage: violation.stage,
        resolution_date: violation.resolution_date,
        status: violation.status,
      });
    }
  }, [location]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => {
    const { name, value } = e.target as HTMLInputElement;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
    setErrors(errors => ({...errors, [name as string]: ''}));
  };

  const handleSelectChange = (e: SelectChangeEvent<string>) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name as string]: value as string,
    }));
  };

  const validateForm = () => {
      const newErrors: { [key: string]: string } = {};
    if (!formData.title) newErrors.title = 'Title is required';
    if (!formData.description) newErrors.description = 'Description is required';
    if (!formData.unit.id) newErrors.unit_selection = 'Unit is required';
    if (!formData.type) newErrors.type = 'Type is required';
    if (formData.type === ViolationType.OTHER && !formData.other_type) newErrors.other_type = 'Other Type is required';
    if (!formData.stage) newErrors.stage = 'Stage is required';
    if (!formData.violation_date) newErrors.violation_date = 'Violation Date is required';
    if (showLastActionDatePicker && !formData.due_date) newErrors.due_date = 'You have selected to add a due date, please provide a due date.';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent, isDraft: boolean = false) => {
    e.preventDefault();
    if (validateForm()) {
      if (location.state && location.state.edit) {
        dispatch(Actions.violation.updateViolationRequestAction.request({violationData: formData}));
      } else {
        if (isDraft) {
          dispatch(Actions.violation.createViolationRequestAction.request({violationData: {
            ...formData,
            status: ViolationStatus.DRAFT,
          }}));
          return;
        }
        dispatch(Actions.violation.createViolationRequestAction.request({violationData: formData}));
      }

      setFormData({
        id: 0,
        title: '',
        description: '',
        possible_penalties: '',
        created_on: new Date().toISOString(),
        updated_on: '',
        violation_date: new Date(),
        due_date: undefined,
        unit: {
          id: 0,
          unit_number: '',
        },
        type: ViolationType.GENERAL,
        other_type: '',
        stage: ViolationStage.WRITTEN_WARNING,
        resolution_date: undefined,
        status: ViolationStatus.OPEN,
      });
      setErrors({});
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
    <Paper elevation={5} sx={{ padding: 6 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography variant="h5" gutterBottom>
          Create Violation
        </Typography>
        <IconButton 
          sx={{ '&:hover': {
            color: 'error.main',
          } }}
          onClick={() => navigate(-1)}>
          <Cancel />
        </IconButton>
      </Box>
      <form onSubmit={handleSubmit}>
        <TextField
          fullWidth
          label="Title"
          variant="outlined"
          name="title"
          placeholder='Please provide a title for the issue.'
          margin='normal'
          value={formData.title}
          onChange={handleChange}
          error={!!errors.title}
          helperText={errors.title}
        />

        <Editor
          value={formData.description}
          placeholder='Please write the details of violation here.'
          error={!!errors.description}
          helperText={errors.description}
          onChange={(value: string) => handleChange({ target: { name: 'description', value } } as React.ChangeEvent<HTMLInputElement>)}
        />

        <Grid2 container spacing={2} sx={{mt: 1}}>
          <Grid2 size={{xs: 12, md: 6}}>
            <DropdownSelection
              dropDownLabel='Type'
              showHeader={false}
              currentValue={formData.type}
              selectionValues={
                ViolationType
                ? Object.keys(ViolationType).map((key) => ({
                  value: ViolationType[key as keyof typeof ViolationType],
                  label: ViolationType[key as keyof typeof ViolationType],
                  color: 'default',
                }))
                : []
              }
              variant='outlined'
              showError={!!errors.type}
              helperText={errors.type}
              handleOnChange={(e) => handleSelectChange({ target: { name: 'type', value: e.target.value } } as SelectChangeEvent<string>)}
            />
            {formData.type === ViolationType.OTHER && (
              <TextField
                fullWidth
                label="Other Type"
                variant="outlined"
                name="other_type"
                margin='dense'
                value={formData.other_type}
                onChange={handleChange}
                error={!!errors.other_type}
                helperText={errors.other_type}
              />
            )}
          </Grid2>
          <Grid2 size={{xs: 12, md: 6}}>
            <DropdownSelection
              dropDownLabel='Stage'
              showHeader={false}
              currentValue={formData.stage}
              selectionValues={
                ViolationStage
                ? Object.keys(ViolationStage).map((key) => ({
                  value: ViolationStage[key as keyof typeof ViolationStage],
                  label: ViolationStage[key as keyof typeof ViolationStage],
                  color: 'default',
                }))
                : []
              }
              showError={!!errors.stage}
              helperText={errors.stage}
              variant='outlined'
              handleOnChange={(e) => handleSelectChange({ target: { name: 'stage', value: e.target.value } } as SelectChangeEvent<string>)}
            />
          </Grid2>
          <Grid2 size={{xs: 12, md: 6}}>
            <DropdownSelection
              dropDownLabel='Unit'
              showHeader={false}
              currentValue={formData.unit.id.toString()}
              selectionValues={unitData.map((unit) => ({
                  value: unit.id.toString(),
                  label: unit.unit_number.toString(),
                  color: 'default',
                  icon: MeetingRoom,
              }))}
              showError={!!errors.unit_selection}
              helperText={errors.unit_selection}
              variant='outlined'
              handleOnChange={(e) => {setFormData((prevState) => ({
                  ...prevState,
                  unit: {
                    ...prevState.unit,
                    id: parseInt(e.target.value as string),
                    unit_number: unitData.find((unit) => unit.id === parseInt(e.target.value as string))?.unit_number.toString() || '',
                  },
                })); setErrors(errors => ({...errors, unit_selection: ''}));}
              }
            />
          </Grid2>
          <Grid2 size={{xs: 12, md: 6}}>
            <DatePicker
              minDate={dayjs().subtract(2, "year")}
              maxDate={dayjs()}
              label="Violation Date"
              value={formData.violation_date ? dayjs(formData.violation_date) : null}
              onChange={(date: dayjs.Dayjs | null) => setFormData((prevState) => {
                setErrors(errors => ({...errors, violation_date: ''}));
                return ({
                ...prevState,
                violation_date: date ? date.toDate() : new Date(),
              })})
              }
              slotProps={{ textField: { fullWidth: true, error: !!errors.violation_date, helperText: errors.violation_date } }}
              format="DD MMM YYYY"
            />
          </Grid2>
          <Grid2 size={{xs: 12, md: 6}} sx={{display: 'flex', alignItems: 'center'}}>
            {!showLastActionDatePicker ? <Link 
              underline="hover"
              sx={{display: 'flex', alignItems: 'center'}}
              onClick={() => setShowLastActionDatePicker(true)} color="primary">
              <AddCircleOutline sx={{mr: 1}} />
              Add Due Date
            </Link>
            :<><DatePicker
              minDate={dayjs()}
              maxDate={dayjs().add(1, "year")}
              label="Due Date"
              value={formData.due_date ? dayjs(formData.due_date) : null}
              onChange={(date: dayjs.Dayjs | null) => {
                setFormData((prevState) => ({
                  ...prevState,
                  due_date: date ? date.toDate() : undefined,
                }));
                setErrors(errors => ({...errors, due_date: ''}));
              }}
              slotProps={{ textField: { fullWidth: true, error: !!errors.due_date, helperText: errors.due_date } }}
              format="DD MMM YYYY"
            /> 
            <IconButton sx={{'&:hover': {color: 'error.main'}}} onClick={() => setShowLastActionDatePicker(false)}>
              <Cancel />
            </IconButton>
            </>}
          </Grid2>
          <Grid2 size={{xs: 12}}>
            <TextField fullWidth
              label="Possible Penalties"
              variant="outlined"
              name="possible_penalties"
              multiline
              rows={2}
              value={formData.possible_penalties}
              onChange={handleChange}
            />
          </Grid2>
        </Grid2>

          {/* <AttachmentUpload /> */}
        <Grid2 container spacing={2} sx={{mt: 2}}>
          <Grid2 size={{xs: 12, md: 6}}>
            <Button fullWidth variant="contained" color="secondary" onClick={(e) => handleSubmit(e, true)}>
                <Save sx={{mr: 2}} /> Save Draft
            </Button>
          </Grid2>
          <Grid2 size={{xs: 12, md: 6}}>
            <Button fullWidth type="submit" variant="contained" color="primary">
                Send <Send sx={{ml: 2}} />
            </Button>
          </Grid2>
        </Grid2>
      </form>
    </Paper>
    </LocalizationProvider>
  );
};

export default CreateViolation;
